import { DatePicker, Drawer, Form } from "antd"
import { Button } from "components"
import { CustomInput, CustomSelect } from "components/new-stuff/form"
import { CloseOutlined } from "@ant-design/icons"
import { newArchAxiosInstance } from "utils/request"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAppNotificationStore, useAuthStore } from "stores"
import { onQueryError } from "utils/utils"
import { useGet } from "utils/useFetch"
import dayjs from "dayjs"
import { useCycleDurationID } from "stores/cycle-duration"
import { CustomSpin } from "components/custom/Loader"
import MultipleObjInfoCard from "../../okrs/component/MultipleObjInfoCard"
import { useState } from "react"
import BulkUploadObj from "../../okrs/BulkUploadObj"
import {
  IPerson,
  IDptListing,
  IDptHod,
  IDptMngData,
  ICompObjListing,
  ISingleDptData,
} from "./types"

interface props {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  dptObjId?: number | null // used when editing
  deptId?: number | null
  role: "admin" | "manager"
}

export const CreateEditDptObj = ({
  open,
  onClose,
  onSuccess,
  dptObjId,
  role,
  deptId,
}: props) => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [openBulkUpload, setOpenBulkUpload] = useState(false)
  const userDeptId = useAuthStore((state) => state.user?.departmentId)

  const cdId = useCycleDurationID({ type: role })

  const selectedDptId = Form.useWatch("departmentId", form)

  // const { isLoading: isLoadingProfile, data: profile } = useGet<IPerson>({
  //   url: `/employee/details/view/index_personal_info`,
  //   instanceKey: "newArchAxiosInstance",
  //   enabled: open && role === "manager",
  //   onSuccess: (data: IPerson) => {
  //     form.setFieldsValue({
  //       departmentId: data?.data?.department_id,
  //     })
  //   },
  // })

  const dptId = role === "manager" ? userDeptId : deptId

  const { isLoading: isLoadingDpt, data: dptListing } = useGet<IDptListing>({
    url:
      role === "manager"
        ? `/api/v2/manager/okr/departments`
        : `/api/v2/${role}/performance/departments`,
    instanceKey: "newArchAxiosInstance",
  })

  const { isLoading: IsLoadingHOD, data: hodData } = useGet<IDptHod>({
    url:
      role === "manager"
        ? `api/v2/manager/okr/hod?departmentId=${selectedDptId}`
        : `api/v2/admin/performance/hod?departmentId=${selectedDptId}`,
    instanceKey: "newArchAxiosInstance",
    onSuccess: (data: IDptHod) => {
      form.setFieldsValue({
        ownerId: data?.data?.headOfDepartmentId,
      })
    },
    enabled: !!selectedDptId,
  })

  const { isLoading: IsLoadingManagers, data: managersListing } =
    useGet<IDptMngData>({
      url: `api/v2/manager/okr/managers?departmentId=${selectedDptId}&pageNumber=-1`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!selectedDptId,
    })

  const { isLoading: isLoadingcompObjListing, data: compObjListing } =
    useGet<ICompObjListing>({
      url: `api/v2/${role}/okr/companyobjectives?cycleDurationId=${cdId}`,
      instanceKey: "newArchAxiosInstance",
      enabled: !!cdId,
    })

  const { isFetching: isLoadingDetails, data } = useGet<ISingleDptData>({
    url: `/api/v2/${role}/okr/department/objectives/${dptObjId}`,
    instanceKey: "newArchAxiosInstance",
    onSuccess: (data: ISingleDptData) => {
      form.setFieldsValue({
        title: data?.data?.departmentObjectiveName,
        companyObjectiveId: data?.data?.companyObjectiveId,
        departmentId: data.data.departmentId,
        ownerId: data.data.owner,
        dueDate: data.data.createdDate ? dayjs(data.data.createdDate) : null,
      })
    },
    enabled: !!dptObjId,
  })

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: async (body: Payload) => {
      const isEdit = !!dptObjId
      const endpoint = isEdit
        ? `/api/v2/${role}/okr/departmentobjective/${dptObjId}`
        : `/api/v2/${role}/okr/departmentobjective`
      const method = isEdit ? "patch" : "post"

      const res = await newArchAxiosInstance.request({
        url: endpoint,
        method,
        data: body,
      })
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || "Operation successful")
        onClose()
        onSuccess?.()

        if (role === "admin") {
          queryClient.invalidateQueries({
            queryKey: ["/api/v2/admin/okr/companyobjectives"],
          })
        } else {
          queryClient.invalidateQueries({
            queryKey: [
              `/api/v2/manager/okr/companyobjectives?cycleDurationId=${cdId}`,
            ],
          })
        }

        queryClient.invalidateQueries({
          queryKey: ["dpt-obj"],
        })
      }
    },
    onError: onQueryError,
  })

  const onFinish = (values: Values) => {
    const body: Payload = {
      companyObjectiveId: values?.companyObjectiveId,
      departmentObjectiveName: values?.title,
      departmentId: values?.departmentId,
      ownerId: values?.ownerId,
      dueDate: values?.dueDate,
      // cycleDurationId: selectedCycle,
    }

    submit(body)
  }

  const managersList =
    managersListing?.data?.docs?.map((mgr) => ({
      label: mgr.fullName,
      value: mgr.personId,
    })) || []

  const hodAlreadyInList = managersList.some(
    (mgr) => mgr.value === hodData?.data?.headOfDepartmentId
  )

  const allOwners = [
    ...managersList,
    ...(!hodAlreadyInList &&
    hodData?.data?.headOfDepartment &&
    hodData?.data?.headOfDepartmentId
      ? [
          {
            label: hodData.data.headOfDepartment,
            value: hodData.data.headOfDepartmentId,
          },
        ]
      : []),
  ]

  return (
    <Drawer
      width={675}
      title={
        <span className=" text-padeBlack font-circular text-xl">
          {dptObjId ? "Edit" : "Add"} Department Objective
        </span>
      }
      open={open}
      onClose={onClose}
      closeIcon={false}
      className="bg-white relative"
      extra={
        <button onClick={onClose}>
          <CloseOutlined className="h-6 w-6" />
        </button>
      }
      footer={
        <div className="flex justify-end gap-4 ">
          <Button
            color="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => form.submit()}
            submitting={isSubmitting}
          >
            {dptObjId ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <CustomSpin spinning={dptObjId ? isLoadingDetails : false}>
        <div className="space-y-4">
          {!dptObjId && (
            <MultipleObjInfoCard
              type="department"
              onClick={() => setOpenBulkUpload(true)}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              departmentId: dptId,
            }}
          >
            <div>
              <CustomInput
                label="Objective"
                name="title"
                required
                placeholder="E.g Increase Revenue"
              />

              <CustomSelect
                label="Align with company goals"
                name="companyObjectiveId"
                required
                isLoading={isLoadingcompObjListing}
                placeholder="Select company goal"
                options={compObjListing?.data?.map((dpt) => ({
                  label: dpt.objectiveName,
                  value: dpt.companyObjectiveId,
                }))}
                showSearch
              />

              <CustomSelect
                label="Department"
                name="departmentId"
                required
                isLoading={isLoadingDpt}
                placeholder="Select Department"
                options={dptListing?.data?.docs?.map((dpt) => ({
                  label: dpt.nameOfDepartment,
                  value: dpt.departmentId,
                }))}
                showSearch
                disabled={role === "manager"}
              />

              <CustomSelect
                label="Owner"
                name="ownerId"
                required
                isLoading={IsLoadingHOD || IsLoadingManagers}
                placeholder="Select Owner"
                options={allOwners ?? []}
                showSearch
                disabled={IsLoadingHOD || IsLoadingManagers}
                // disabled={role === "admin"}
              />

              <Form.Item
                name="dueDate"
                className="w-full flex gap-2 flex-col"
                label={
                  <span className="text-padeBlack font-circular text-sm">
                    Due Date
                  </span>
                }
                required
              >
                <DatePicker
                  className="w-full"
                  placeholder="Date"
                  disabledDate={(date) => {
                    return date.isBefore(dayjs())
                  }}
                />
              </Form.Item>
            </div>
          </Form>
        </div>
      </CustomSpin>

      <BulkUploadObj
        open={openBulkUpload}
        handleClose={() => setOpenBulkUpload(false)}
        selectedCycle={Number(cdId)}
        handleOnSuccess={() => {
          setOpenBulkUpload(false)
          onClose()
        }}
        type={"department"}
        role={role}
      />
    </Drawer>
  )
}

interface Values {
  title: string
  departmentId: number
  ownerId: number
  companyObjectiveId: number
  dueDate: Date
}

interface Payload {
  companyObjectiveId: number
  departmentObjectiveName: string
  departmentId: number
  ownerId: number
  // cycleDurationId: selectedCycle,
  dueDate: Date
}
